import { minorUnitsToCostProperty } from '@apps/stays/components/OfferSummary/utils';
import {
  ExtendedAccommodationCatalogPropertyFragment,
  TravelCurrencyCode,
  TravelJoyPlacePricingAndBookingDetailsFragment,
  TravelPlacePricingAndBookingDetailsFragment,
} from '@graphql/generated';
import { applyDiscount, Discount } from '@shared/components/DiscountsProvider';
import { LocaleContextType } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';

/**
 * Rounds up minor units to the nearest whole number for display purposes
 * @param valueInMinorUnits - The price value in minor units (e.g., cents)
 * @param digits - The number of decimal digits (e.g., 2 for cents)
 * @returns The rounded up value in minor units
 * @example roundUpMinorUnits(37983, 2) => 38000 (379.83 -> 380.00)
 */
export const roundUpMinorUnits = (valueInMinorUnits: number, digits: number): number => {
  const divisor = Math.pow(10, digits);
  return Math.ceil(valueInMinorUnits / divisor) * divisor;
};

export const translatedPrice = (priceInMinorUnits: number, digits: number, currency: string, language: NavigatorLanguage['language'] = 'en-US', showDecimals: boolean = false) => {
  const locale = currency === TravelCurrencyCode.USD && language.startsWith('en-US') ? 'en-CA' : language;
  const minorUnits = showDecimals ? priceInMinorUnits : roundUpMinorUnits(priceInMinorUnits, digits);
  return (minorUnits / Math.pow(10, digits)).toLocaleString(locale, {
    style: 'currency',
    ...(showDecimals ? { maximumFractionDigits: digits, minimumFractionDigits: digits } : { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
    currency,
  });
};
export const getPercentageOff = (
  minimumPercentageOffToDisplay: number,
  priceInMinorUnits?: number,
  strikeOutPriceInMinorUnits?: number,
  priceDigits?: number,
  strikeOutPriceDigits?: number,
): number | undefined => {
  if (!priceInMinorUnits || !strikeOutPriceInMinorUnits || !priceDigits || !strikeOutPriceDigits) return undefined;

  const price = priceInMinorUnits / Math.pow(10, priceDigits);
  const strikeOutPrice = strikeOutPriceInMinorUnits / Math.pow(10, strikeOutPriceDigits);
  const percentageOff = Math.round(((strikeOutPrice - price) / strikeOutPrice) * 100);
  return percentageOff >= minimumPercentageOffToDisplay ? percentageOff : undefined;
};

export const getCalculatedPrice = (
  priceDataFromApi: TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment | ExtendedAccommodationCatalogPropertyFragment | null,
  locale: LocaleContextType,
  minimumPercentageOffToDisplay: number,
  travelPlaceId?: string | null,
  complaintRef?: React.MutableRefObject<boolean>,
  generalPercentDiscount?: Discount,
) => {
  const priceData = priceDataFromApi?.pricingInfo?.nightlyBaseRate;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price =
    travelPlaceId && minorUnits && currency
      ? minorUnitsToCostProperty({ valueInMinorUnits: applyDiscount(minorUnits, generalPercentDiscount), currency, digits: minorUnitsDigits }, locale.language).display
      : undefined;
  const originalPrice =
    travelPlaceId && minorUnits && currency && minorUnitsDigits
      ? minorUnitsToCostProperty({ valueInMinorUnits: minorUnits, digits: minorUnitsDigits, currency }, locale.language).display
      : undefined;
  const strikeOutPriceData = priceDataFromApi?.pricingInfo?.strikeoutNightlyBaseRate;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  if (strikeOutMinorUnits && !strikeOutPriceValid && !complaintRef?.current) {
    if (complaintRef) {
      complaintRef.current = true;
    }
    addError(new Error('Strikeout price is not valid'), {
      context: 'useTravelPlacesPricingAndBookingDetailsFragment',
      strikeOutPriceUnits: strikeOutMinorUnits,
      priceUnits: minorUnits,
      travelPlaceId: travelPlaceId,
    });
  }
  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(roundUpMinorUnits(strikeOutMinorUnits, strikeOutMinorUnitsDigits!), strikeOutMinorUnitsDigits!, strikeOutCurrency, locale.language)
      : undefined;

  const percentageOff = getPercentageOff(minimumPercentageOffToDisplay, minorUnits, strikeOutMinorUnits, minorUnitsDigits, strikeOutMinorUnitsDigits);

  const totalPriceData = priceDataFromApi?.pricingInfo?.totalRate;
  const totalMinorUnits = totalPriceData?.valueInMinorUnits;
  const totalMinorUnitsDigits = totalPriceData?.digits ?? 2;
  const totalCurrency = totalPriceData?.currency;
  const totalAmount =
    (travelPlaceId &&
      (totalMinorUnits && totalCurrency
        ? translatedPrice(roundUpMinorUnits(applyDiscount(totalMinorUnits, generalPercentDiscount), totalMinorUnitsDigits), totalMinorUnitsDigits, totalCurrency, locale.language)
        : null)) ||
    undefined;

  return { price, strikeOutPrice, percentageOff, minorUnits, totalAmount, originalPrice };
};
